import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, Mail, X, MapPin, Linkedin, Instagram } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { NAV_LINKS, CONTACT, ASSETS, SOCIAL } from "../mock";
import { Container, WhatsappIcon } from "./ui-kit";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <>
      {/* Üst bilgi şeridi */}
      <div className="hidden md:block border-b border-line bg-sand" data-testid="topbar">
        <Container className="flex items-center justify-between h-9 text-[12px] text-ink2">
          <p className="flex items-center gap-2">
            <MapPin size={12} className="text-brand" />
            Güngören Fabrika · Merter Şubesi · İstanbul
          </p>
          <div className="flex items-center gap-6">
            <a href={CONTACT.phoneHref} className="flex items-center gap-1.5 hover:text-ink transition-colors" data-testid="topbar-phone">
              <Phone size={12} className="text-brand" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.emails[0]}`} className="flex items-center gap-1.5 hover:text-ink transition-colors" data-testid="topbar-email">
              <Mail size={12} className="text-brand" /> {CONTACT.emails[0]}
            </a>
            <span className="w-px h-4 bg-line" />
            <div className="flex items-center gap-3.5">
              <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp Sipariş Hattı" className="text-ink2 hover:text-[#25D366] transition-colors" data-testid="topbar-whatsapp">
                <WhatsappIcon size={13} />
              </a>
              <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-ink2 hover:text-[#E1306C] transition-colors" data-testid="topbar-instagram">
                <Instagram size={13} />
              </a>
              <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-ink2 hover:text-[#0A66C2] transition-colors" data-testid="topbar-linkedin">
                <Linkedin size={13} />
              </a>
            </div>
          </div>
        </Container>
      </div>

      {/* Ana header */}
      <header
        className={`sticky top-0 z-50 bg-canvas/85 backdrop-blur-xl transition-all duration-300 border-b ${
          scrolled ? "border-line shadow-[0_10px_40px_rgba(26,26,26,0.06)]" : "border-transparent"
        }`}
        data-testid="navbar"
      >
        <Container className="flex items-center justify-between h-[78px]">
          <Link to="/" className="flex items-center shrink-0" aria-label="Koşan Düğme Anasayfa" data-testid="nav-logo">
            <img src={ASSETS.logoFull} alt="Koşan Düğme" className="h-9 sm:h-10 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9" data-testid="nav-desktop">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `relative text-[12px] font-semibold tracking-[0.16em] uppercase transition-colors py-2 group ${
                    isActive ? "text-ink" : "text-ink2 hover:text-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute left-1/2 -translate-x-1/2 -bottom-1 w-1 h-1 rounded-full bg-brand transition-all duration-300 ${
                        isActive ? "opacity-100 scale-100" : "opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/iletisim"
              data-testid="nav-cta"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full border border-ink/20 text-[12px] font-semibold tracking-[0.12em] uppercase text-ink hover:bg-ink hover:border-ink hover:text-white transition-all duration-300"
            >
              Teklif Al
            </Link>
          </div>

          {/* Mobil menü */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 -mr-2 text-ink" aria-label="Menüyü aç" data-testid="nav-mobile-open">
                <Menu size={24} strokeWidth={1.6} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] bg-canvas border-l border-line p-0" data-testid="nav-mobile-sheet">
              <div className="flex items-center justify-between px-6 h-[78px] border-b border-line">
                <img src={ASSETS.logoFull} alt="Koşan Düğme" className="h-9 w-auto" />
                <SheetClose asChild>
                  <button className="text-ink2 hover:text-ink p-1" aria-label="Menüyü kapat" data-testid="nav-mobile-close">
                    <X size={22} strokeWidth={1.6} />
                  </button>
                </SheetClose>
              </div>
              <nav className="flex flex-col px-6 py-6">
                {NAV_LINKS.map((l, i) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={`nav-mobile-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                      className={({ isActive }) =>
                        `py-4 text-[15px] font-medium border-b border-line flex items-center justify-between transition-colors ${
                          isActive ? "text-brand" : "text-ink hover:text-brand"
                        }`
                      }
                    >
                      {l.label}
                      <span className="text-mute text-[11px] tracking-[0.2em]">0{i + 1}</span>
                    </NavLink>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <Link
                    to="/iletisim"
                    data-testid="nav-mobile-cta"
                    className="mt-8 inline-flex justify-center items-center h-12 rounded-full bg-brand hover:bg-brandDark text-white text-[13px] font-semibold tracking-[0.06em] transition-colors"
                  >
                    Teklif Al
                  </Link>
                </SheetClose>
                <div className="mt-8 text-[13px] text-ink2 space-y-2">
                  <a href={CONTACT.phoneHref} className="flex items-center gap-2"><Phone size={13} className="text-brand" /> {CONTACT.phone}</a>
                  <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2"><WhatsappIcon size={13} className="text-[#25D366]" /> WhatsApp · {CONTACT.whatsapp}</a>
                  <a href={`mailto:${CONTACT.emails[0]}`} className="flex items-center gap-2"><Mail size={13} className="text-brand" /> {CONTACT.emails[0]}</a>
                  <div className="flex items-center gap-4 pt-3">
                    <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-ink2 hover:text-[#E1306C] transition-colors"><Instagram size={17} /></a>
                    <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-ink2 hover:text-[#0A66C2] transition-colors"><Linkedin size={17} /></a>
                  </div>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </Container>
      </header>
    </>
  );
};

export default Navbar;
